import { TONE_HEX } from '../../lib/domain';

/**
 * Part-to-whole in one horizontal bar.
 *
 * This replaces the donut a fleet dashboard usually reaches for. A donut is
 * only honest for part-to-whole at a glance with few segments, and it makes
 * close values genuinely hard to compare — which is exactly what "how many
 * are moving versus idle" asks you to do. A single bar plus a labelled legend
 * answers it better in less space.
 *
 * Segments are separated by a 2px gap in the surface colour rather than a
 * stroke. A stroke adds ink that is not data; the gap is the mechanism.
 */
export function StackedBar({ segments, total, height = 10, showLegend = true, className = '' }) {
  const sum = total ?? segments.reduce((acc, s) => acc + (s.value || 0), 0);
  const present = segments.filter((s) => (s.value || 0) > 0);

  if (!sum) {
    return (
      <div className={className}>
        <div className="w-full rounded-full bg-surface-3" style={{ height }} />
        <p className="mt-2 text-[12.5px] text-ink-3">Nothing reporting yet.</p>
      </div>
    );
  }

  return (
    <div className={className}>
      <div
        className="flex w-full overflow-hidden rounded-full bg-surface-3"
        style={{ height, gap: '2px' }}
        role="img"
        aria-label={present.map((s) => `${s.label}: ${s.value}`).join(', ')}
      >
        {present.map((segment) => (
          <div
            key={segment.key || segment.label}
            title={`${segment.label}: ${segment.value}`}
            style={{
              width: `${(segment.value / sum) * 100}%`,
              background: TONE_HEX[segment.tone] || TONE_HEX.neutral,
            }}
            className="h-full transition-[width] duration-500 first:rounded-l-full last:rounded-r-full"
          />
        ))}
      </div>

      {showLegend && (
        <ul className="mt-3 grid grid-cols-2 gap-x-4 gap-y-1.5 sm:grid-cols-3">
          {segments.map((segment) => (
            <li key={segment.key || segment.label} className="flex items-center gap-1.5">
              <span
                className="h-2 w-2 shrink-0 rounded-full"
                style={{ background: TONE_HEX[segment.tone] || TONE_HEX.neutral }}
                aria-hidden="true"
              />
              <span className="min-w-0 flex-1 truncate text-[12.5px] text-ink-2">
                {segment.label}
              </span>
              <span className="text-[12.5px] font-semibold text-ink tabular-nums">
                {segment.value}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * A ranked horizontal bar list — firmware versions, alert kinds, operators.
 * Values are direct-labelled at the bar end, so no axis is needed at all.
 */
export function BarList({ items, formatValue = (v) => String(v), color, className = '' }) {
  const max = Math.max(1, ...items.map((item) => Number(item.value) || 0));

  return (
    <ul className={`space-y-2 ${className}`}>
      {items.map((item) => (
        <li key={item.key || item.label}>
          <div className="flex items-baseline justify-between gap-3">
            <span className="min-w-0 truncate text-[13px] text-ink-2">{item.label}</span>
            <span className="text-[13px] font-semibold text-ink tabular-nums">
              {formatValue(item.value)}
            </span>
          </div>
          <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-surface-3">
            <div
              className="h-full rounded-full transition-[width] duration-500"
              style={{
                width: `${((Number(item.value) || 0) / max) * 100}%`,
                background: item.tone ? TONE_HEX[item.tone] : color || 'var(--series-1)',
              }}
            />
          </div>
        </li>
      ))}
    </ul>
  );
}
